import { defineMessages, MessageDescriptor } from "react-intl";

const validationErrors = defineMessages({
  allStopsInaccessible: {
    id: "routes.validationError.v0774",
    defaultMessage: "All stops are no longer accessible.",
    description: "Route validation error when no entity stop can be read by the user",
  },
  databaseConflict: {
    id: "routes.validationError.pg-23505",
    defaultMessage: "Database conflict. Please retry.",
    description: "Route validation error for a wrapped pg-23505 unique-violation race",
  },
  duplicateStop: {
    id: "routes.validationError.v0386",
    defaultMessage: "Duplicate stop.",
    description:
      "Route validation error when the same stop appears twice in one batch — v0386, v0935",
  },
  invalidId: {
    id: "routes.validationError.v0404",
    defaultMessage: "Invalid stop id.",
    description:
      "Route validation error when a bulk stop update or delete carries a non-integer id — v0404",
  },
  routeFormatInvalid: {
    id: "routes.validationError.v0276",
    defaultMessage: "Route format is invalid.",
    description:
      "Route validation error when the route payload fails backend validation — v0276; logged to Sentry",
  },
  routeNotBuilt: {
    id: "routes.validationError.v0281",
    defaultMessage:
      "No route could be found between the starting point, stops and the ending point.",
    description: "Route validation error when no route could be found — v0278, v0281, v0349",
  },
  someStopsInaccessible: {
    id: "routes.validationError.v0771",
    defaultMessage: "Some stops are no longer accessible.",
    description: "Route validation error when some entity stops cannot be read by the user",
  },
  tooManyStops: {
    id: "routes.validationError.v0282",
    defaultMessage: "This route has too many stops. Please remove some and try again.",
    description: "Route validation error when the route exceeds the maximum stop count — v0282",
  },
});

export type RouteValidationErrorCode =
  | "pg-23505"
  | "v0276"
  | "v0278"
  | "v0281"
  | "v0282"
  | "v0349"
  | "v0386"
  | "v0404"
  | "v0771"
  | "v0774"
  | "v0935";

// Keyed by the platform code the server raises, so the whole map can be handed to
// getErrorMessage / handleError as messageByErrorCodeMap. Several codes share one
// descriptor where the copy is identical.
export const routeValidationErrorMessages: Readonly<
  Record<RouteValidationErrorCode, MessageDescriptor>
> = {
  "pg-23505": validationErrors.databaseConflict,
  v0276: validationErrors.routeFormatInvalid,
  v0278: validationErrors.routeNotBuilt,
  v0281: validationErrors.routeNotBuilt,
  v0282: validationErrors.tooManyStops,
  v0349: validationErrors.routeNotBuilt,
  v0386: validationErrors.duplicateStop,
  v0404: validationErrors.invalidId,
  v0771: validationErrors.someStopsInaccessible,
  v0774: validationErrors.allStopsInaccessible,
  v0935: validationErrors.duplicateStop,
};

export const routeActionMessages = defineMessages({
  deleteFailure: {
    id: "routes.delete.failure",
    defaultMessage: "Failed to delete route.",
    description: "Error toast when deleting a route fails",
  },
  deleteSuccess: {
    id: "routes.delete.success",
    defaultMessage: "Route deleted.",
    description: "Success toast after a route is deleted",
  },
  duplicateFailure: {
    id: "routes.duplicate.failure",
    defaultMessage: "Failed to duplicate route.",
    description: "Error toast when duplicating a route fails",
  },
  duplicatePending: {
    id: "routes.duplicate.pending",
    defaultMessage: "Duplicating route…",
    description: "Loading toast shown while a route is being duplicated",
  },
  duplicateSuccess: {
    id: "routes.duplicate.success",
    defaultMessage: "Route duplicated.",
    description: "Success toast after a route is duplicated",
  },
});
